'use client';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Card, CardContent, CardHeader } from '@eliadmin/core/src/components/ui/card';
import Link from '@eliadmin/core/src/components/link';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import type { HTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  ChevronDown as LuChevronDown,
  ChevronLeft as LuChevronLeft,
  ChevronRight as LuChevronRight,
} from 'lucide-react';
import { CategorySliderChildCategory } from '@eliadmin/core/src/components/header/category-slider-child-category';
import { Container } from '@eliadmin/core/src/components/ui/container';

const CATEGORY_DROPDOWN_OPEN_DELAY_MS = 180;

export type CategorySliderClassName = {
  main?: string;
  controlButton?: string;
  list?: string;
  listItem?: string;
  listItemChevron?: string;
  link?: string;

  hoverCard?: string;
  hoverCardHeader?: string;
  hoverCardHeaderTitle?: string;
  hoverCardContent?: string;
  hoverCardList?: string;
  hoverCardListItem?: string;
  hoverCardLink?: string;
};

export interface CategorySliderProps {
  className?: CategorySliderClassName;
  style?: HTMLAttributes<HTMLElement>['style'];
  categories?: CategoriesEntity[];
  expandable?: boolean;
}

export const CategorySlider = ({
  className,
  style,
  categories: propsCategories,
  expandable = false,
}: CategorySliderProps) => {
  const containerRef = useRef<HTMLUListElement | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
  const hoverDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showButtons, setShowButtons] = useState(false);
  const categories = propsCategories;

  useGlobalStore(state => state.generalLayout);

  const scroll = (delta: number) => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const currentScrollLeft = container.scrollLeft;
    container.scrollTo({
      left: currentScrollLeft + delta,
      behavior: 'smooth',
    });
  };

  const handleCategoryHover = (index: number | null) => {
    if (hoverDelayRef.current) {
      clearTimeout(hoverDelayRef.current);
      hoverDelayRef.current = null;
    }

    if (index === null) {
      setHoveredCategory(null);
      return;
    }

    if (hoveredCategory !== null) {
      setHoveredCategory(index);
      return;
    }

    hoverDelayRef.current = setTimeout(() => {
      setHoveredCategory(index);
      hoverDelayRef.current = null;
    }, CATEGORY_DROPDOWN_OPEN_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (hoverDelayRef.current) {
        clearTimeout(hoverDelayRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateButtonVisibility = () => {
      setShowButtons(container.scrollWidth > container.clientWidth + 1);
    };

    updateButtonVisibility();

    const resizeObserver = new ResizeObserver(() => {
      updateButtonVisibility();
    });

    resizeObserver.observe(container);
    window.addEventListener('resize', updateButtonVisibility);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateButtonVisibility);
    };
  }, [categories]);

  return (
    // <div
    //   className={cn([
    //     'flex h-8 w-full flex-row items-center justify-center bg-foreground text-background',
    //     className,
    //   ])}
    // >
    //   <Container size="xl" className={'relative flex items-center justify-center'}>
    <nav
      className={cn(
        'relative flex items-center justify-center',
        'ea-category-slider__main',
        className?.main,
      )}
      style={style}
    >
      {showButtons && (
        <LuChevronLeft
          onClick={() => scroll(-100)}
          className={cn(
            'mr-auto h-6 w-6 shrink-0 cursor-pointer',
            'ea-category-slider__control-button',
            className?.controlButton,
          )}
        />
      )}
      <ul
        ref={containerRef}
        className={cn(
          'hide-scroll-bar group flex w-full flex-row overflow-x-scroll will-change-scroll',
          'ea-category-slider__list',
          className?.list,
        )}
      >
        {categories?.map((category, index) => (
          <li
            key={index}
            className={cn(
              'flex min-w-max flex-1 flex-row items-stretch justify-center border-r transition-all first:border-l',
              'ea-category-slider__list-item',
              className?.listItem,
            )}
            onMouseEnter={() => {
              if (!category.childs?.length) return;
              if (!expandable) return;
              handleCategoryHover(index);
            }}
            onMouseLeave={() => {
              if (!category.childs?.length) return;
              if (!expandable) return;
              handleCategoryHover(null);
            }}
          >
            <Link
              href={category.slug}
              prefetchOnHover
              onClick={() => handleCategoryHover(null)}
              className={cn(
                'flex w-full flex-row items-center justify-center gap-2 px-2 py-1 hover:bg-accent hover:underline',
                'ea-category-slider__list-item-link',
                className?.link,
              )}
            >
              {category.icon && (
                <Image
                  alt=""
                  src={category.icon.url}
                  width={96}
                  height={96}
                  style={{ width: 28, height: 28, objectFit: 'contain' }}
                />
              )}
              {category.name}
              {!!category.childs?.length && expandable && (
                <LuChevronDown
                  className={cn(
                    'ml-1 inline-block',
                    'ea-category-slider__list-item-chevron',
                    className?.listItemChevron,
                  )}
                  size={16}
                />
              )}
            </Link>

            {hoveredCategory === index && category.childs && (
              <Card
                className={cn(
                  'custom-scrollbar absolute right-0 top-8 z-10 max-h-[500px] w-full overflow-y-auto rounded-xl bg-background text-primary-foreground shadow-xl',
                  'ea-category-slider__hover-card',
                  className?.hoverCard,
                )}
              >
                <CardHeader
                  className={cn(
                    'flex flex-row items-center justify-between bg-background px-4 py-2 text-foreground',
                    'ea-category-slider__hover-card-header',
                    className?.hoverCardHeader,
                  )}
                >
                  <Container>
                    <h4 className={cn('mb-2 text-lg font-bold', className?.hoverCardHeader)}>
                      {category.name}
                    </h4>
                  </Container>
                </CardHeader>
                <CardContent
                  className={cn(
                    'ea-category-slider__hover-card-content bg-background text-foreground',
                    className?.hoverCardContent,
                  )}
                >
                  <Container>
                    <ul
                      className={cn(
                        'grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3',
                        'ea-category-slider__hover-card-list',
                        className?.hoverCardList,
                      )}
                      onClick={e => {
                        const target = e.target as HTMLElement;
                        if (target.closest('a')) {
                          setHoveredCategory(null);
                        }
                      }}
                    >
                      {category.childs.map(childCategory => (
                        <CategorySliderChildCategory
                          key={childCategory.slug}
                          category={category}
                          childCategory={childCategory}
                          className={className}
                        />
                      ))}
                    </ul>
                  </Container>
                </CardContent>
              </Card>
            )}
          </li>
        ))}
      </ul>
      {showButtons && (
        <LuChevronRight
          onClick={() => scroll(100)}
          className={cn(
            'ml-auto h-6 w-6 shrink-0 cursor-pointer',
            'ea-category-slider__control-button',
            className?.controlButton,
          )}
        />
      )}
    </nav>
    //   </Container>
    // </div>
  );
};
